import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Minus, Flower2, Gem } from "lucide-react";
import { toast } from "sonner";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { ZoomImage } from "@/components/ZoomImage";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setActive(0); }).catch(() => nav("/shop"));
  }, [slug, nav]);

  if (!product) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>;

  const handleAdd = () => {
    add(product, qty);
    toast.success(`${product.name} aggiunto allo scrigno`);
  };

  return (
    <div className="relative z-10 pt-28 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="back-to-shop">
        <ArrowLeft size={16} /> Torna alla collezione
      </Link>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Images */}
        <div className="space-y-4">
          <ZoomImage key={active} src={product.images?.[active]} alt={product.name} />
          <p className="text-xs text-muted-foreground text-center hidden md:block">Passa il mouse per ingrandire · clicca per schermo intero</p>
          {product.images?.length > 1 && (
            <div className="flex gap-4">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`h-24 w-20 overflow-hidden rounded-md transition-opacity ${active === i ? "ring-2 ring-gold" : "opacity-60 hover:opacity-100"}`} data-testid={`thumb-${i}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info sticky */}
        <div className="md:sticky md:top-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <h1 className="font-serif text-5xl md:text-6xl font-light leading-none mb-4" data-testid="product-title">{product.name}</h1>
            <p className="text-2xl text-gold mb-8" data-testid="product-price">{eur(product.price)}</p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10">{product.description}</p>

            <div className="grid grid-cols-2 gap-6 mb-10 border-y border-border py-6">
              <div className="flex items-center gap-3">
                <Flower2 size={20} className="text-primary" />
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Fiore</p><p className="text-sm">{product.flower}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <Gem size={20} className="text-[hsl(var(--lavender))]" />
                <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Cristallo</p><p className="text-sm">{product.crystal}</p></div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 border border-border rounded-full px-4 py-2">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec"><Minus size={16} /></button>
                <span className="w-6 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} data-testid="qty-inc"><Plus size={16} /></button>
              </div>
              <Button className="flex-1 rounded-full bg-gold hover:bg-gold/90 text-white h-14 text-base" onClick={handleAdd} data-testid="add-to-cart-btn">
                Aggiungi allo scrigno
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
